import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';
import { Box, Typography } from '@tq/ui-components';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';

import { SubmitResponseRequest, useSubmitResponse } from 'api';
import { NumberBox, FileDragDrop } from 'components';
import {
  responseFilesState,
  responseSupportingFilesState,
  responseUploadRequiredState,
} from 'store';
import { StandardResponseForm } from '..';
import { useResponse } from '../../hooks';
import { ConsultationResponseProps } from '../../types';
import { MAX_FILE_COUNT } from '../../../../constants';
import { FileInformation } from '../../../../components/FileDragDrop/components/FileInformation';

export const StandardResponse: React.FC<ConsultationResponseProps> = ({ consultation }) => {
  const { t } = useTranslation('consultation_response');
  const [uploadRequired, setUploadRequired] = useState(false);
  const headingFontSize = 16;

  const { response, isLoading } = useResponse(consultation.id);
  const { mutate: submitResponse } = useSubmitResponse();

  const setUploadRequiredState = useSetRecoilState(responseUploadRequiredState);
  const setResponseFiles = useSetRecoilState(responseFilesState);
  const setSupportingFiles = useSetRecoilState(responseSupportingFilesState);

  useEffect(() => {
    if (!isLoading && !response) {
      submitResponse({
        consultationId: consultation.id,
      } as SubmitResponseRequest);
    }
  }, [isLoading, response, submitResponse, consultation.id]);

  useEffect(() => {
    setUploadRequiredState(uploadRequired);
  }, [setUploadRequiredState, uploadRequired]);

  return (
    <>
      <Box
        px={{ base: 0, lg: 6 }}
        py={{ base: 4, lg: 6 }}
        pt={{ base: 6, lg: 10 }}
        pb={{ base: 'unset', lg: 0 }}
        borderBottom={`1px solid ${COLOURS.gray[400]}`}
        gridColumn={{ base: '1 / span 2', lg: '1' }}
        overflow='visible'
      >
        <Box pb={{ base: 0, lg: 6 }}>
          <NumberBox label={1} />
          <Typography display='inline' verticalAlign='middle' fontSize={headingFontSize}>
            <strong>{t('set_response_type', 'Set response type')}</strong>
          </Typography>
        </Box>
        <StandardResponseForm
          consultation={consultation}
          setUploadRequired={setUploadRequired}
        />
      </Box>
      <Box pb={0} p={{ base: 0, lg: 6 }} mt={5}>
        <FileInformation />
      </Box>
      <Box p={{ base: 0, lg: '2rem' }} borderBottom={`1px solid ${COLOURS.gray[400]}`} my={5}>
        <NumberBox label={2} />
        <Typography display='inline' verticalAlign='middle' fontSize={headingFontSize}>
          <strong>{t('upload_response', 'Upload response')}</strong>
        </Typography>
        {uploadRequired && (
          <span style={{ width: 'max-content', marginLeft: '8px' }}>
            <span
              className='label__required-indicator'
              role='presentation'
              aria-hidden='true'
              style={{ color: `${COLOURS.red[400]}` }}
            >
              *
            </span>
            <span className='label__required-text'>Required</span>
          </span>
        )}
        <Box my={4}>
          <FileDragDrop
            className='file-input'
            name='response-file-upload'
            bg={{ base: COLOURS.blue[150], md: COLOURS.gray[100] }}
            border={{
              base: `2px dashed ${COLOURS.blue[600]}`,
              md: `1px solid ${COLOURS.gray[300]}`,
            }}
            groupingReference={response?.referenceNumber}
            setUploadedFiles={setResponseFiles}
            w='100%'
            maxWidth={{ base: '100%', lg: 876 }}
            maxFiles={MAX_FILE_COUNT}
            showFileInfoMessage={false}
          />
        </Box>
      </Box>
      <Box p={{ base: 0, lg: 6 }} my={5}>
        <NumberBox label={3} />
        <Typography display='inline' verticalAlign='middle' fontSize={headingFontSize}>
          <strong>
            {t('upload_supporting_documents', 'Upload supporting documents (optional)')}
          </strong>
        </Typography>
        <Box my={4}>
          <FileDragDrop
            groupingReference={response?.referenceNumber}
            multiple
            setUploadedFiles={setSupportingFiles}
            w='100%'
            maxWidth={{ base: '100%', lg: 876 }}
            showFileInfoMessage={false}
          />
        </Box>
      </Box>
    </>
  );
};
